"use client";

import { useState } from "react";
import { Check, ChevronRight, Loader2, RefreshCcw, Target, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { api, type QuizSession, type QuizSessionQuestion, type QuizResult } from "@/lib/api";
import NoteDrawer from "@/components/NoteDrawer";

type Phase = "intro" | "loading" | "answering" | "result";

export default function QuizCenter() {
  const [phase, setPhase] = useState<Phase>("intro");
  const [session, setSession] = useState<QuizSession | null>(null);
  const [current, setCurrent] = useState(0);
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [revealed, setRevealed] = useState(false);
  const [result, setResult] = useState<QuizResult | null>(null);
  const [error, setError] = useState("");
  const [count, setCount] = useState(10);

  async function start(count = 10) {
    setPhase("loading");
    setError("");
    setResult(null);
    setPicks({});
    try {
      const data = await api.quizStart(count);
      if (!data.questions?.length) throw new Error("empty");
      setSession(data);
      setCurrent(0);
      setRevealed(false);
      setPhase("answering");
    } catch {
      setError("测验启动失败，请确认后端已启动。");
      setPhase("intro");
    }
  }

  async function submit() {
    setPhase("loading");
    try {
      const data = await api.quizSubmit(session?.session_id ?? 0, Object.entries(picks).map(([id, answer]) => ({ exercise_id: id, answer })));
      setResult(data);
      setPhase("result");
    } catch {
      setError("提交失败，请重试。");
      setPhase("answering");
    }
  }

  function pick(q: QuizSessionQuestion, opt: string) {
    if (revealed) return;
    setPicks((p) => ({ ...p, [q.exercise_id]: opt }));
    setRevealed(true);
  }

  const q = session?.questions[current];
  const myPick = q ? picks[q.exercise_id] : undefined;
  const resultById = new Map((result?.results || []).map((r) => [r.exercise_id, r]));

  return (
    <main className="mx-auto max-w-5xl px-4 py-4 sm:py-8">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-xl font-bold tracking-tight sm:text-2xl">能力自测</h1>
        <p className="mt-0.5 text-[13px] text-muted-foreground">
          跨章节随机抽题 · 答完即时判分并可回看解析
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {phase === "intro" && (
        <div className="flex flex-col items-center gap-6 py-8 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <Target className="h-7 w-7 text-primary" />
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">
            从全部课程的练习题中随机抽题，检验整体掌握度。适合学完一章后检验、或考前自测。
          </p>
          {/* iOS 分段控件选题目数量（真实选中态） */}
          <div className="flex h-12 w-full max-w-xs items-center rounded-lg bg-muted p-1">
            {[
              { n: 5, label: "5 题" },
              { n: 10, label: "10 题" },
              { n: 15, label: "15 题" },
            ].map((o) => (
              <button
                key={o.n}
                onClick={() => setCount(o.n)}
                aria-pressed={count === o.n}
                className={cn(
                  "h-10 flex-1 rounded-md text-sm font-medium transition-all",
                  count === o.n
                    ? "bg-card text-primary shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {o.label}
              </button>
            ))}
          </div>
          <Button onClick={() => start(count)} className="h-11 w-full max-w-xs">
            开始测验
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      )}

      {phase === "loading" && (
        <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {session ? "正在判分..." : "正在抽题..."}
        </div>
      )}

      {phase === "answering" && q && (
        <div>
          <div className="mb-3 flex items-center justify-between text-xs text-muted-foreground">
            <span className="tabular-nums">
              第 {current + 1} / {session!.total} 题
            </span>
            <span className="rounded bg-muted px-2 py-0.5">{q.chapter}</span>
          </div>
          <div className="mb-5 h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${((current + (revealed ? 1 : 0)) / session!.total) * 100}%` }}
            />
          </div>

          <h3 className="mb-5 text-base font-bold leading-relaxed">{q.question}</h3>

          <div className="grouped-list">
            {q.options.map((opt) => {
              const letter = opt.charAt(0);
              const isMyPick = myPick === opt;
              return (
                <button
                  key={opt}
                  onClick={() => pick(q, opt)}
                  disabled={revealed}
                  className={cn(
                    "flex w-full min-h-12 items-center gap-3 px-4 py-3 text-left text-sm transition-colors",
                    !revealed && "cursor-pointer active:bg-muted/70",
                    revealed && isMyPick && "bg-primary/5"
                  )}
                >
                  <span
                    className={cn(
                      "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold",
                      revealed && isMyPick ? "border-primary bg-primary text-primary-foreground" : "border-input text-muted-foreground"
                    )}
                  >
                    {letter}
                  </span>
                  <span className="pt-px">{opt}</span>
                </button>
              );
            })}
          </div>

          {revealed && current < session!.total - 1 && (
            <div className="mt-5 flex justify-end">
              <Button
                onClick={() => {
                  setCurrent((c) => c + 1);
                  setRevealed(false);
                }}
                className="h-11"
              >
                下一题
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
            )}

            {revealed && current === session!.total - 1 && (
              <div className="mt-4 flex justify-end">
                <Button onClick={submit} disabled={Object.keys(picks).length < session!.total}>
                  提交判分
                </Button>
              </div>
            )}
        </div>
      )}

      {phase === "result" && result && (
        <Card className="shadow-sm">
          <div className="h-1 bg-gradient-to-r from-success/60 to-warning/60" />
          <CardContent className="py-6">
            <div
              className={cn(
                "mb-5 flex flex-col items-center gap-2 rounded-lg border py-6 text-center",
                result.correct === result.total
                  ? "border-success/40 bg-success/10"
                  : result.correct >= Math.ceil(result.total / 2)
                    ? "border-primary/30 bg-primary/5"
                    : "border-destructive/30 bg-destructive/5"
              )}
            >
              <div className="text-3xl font-bold">
                {result.correct} / {result.total}
              </div>
              <div className="text-sm text-muted-foreground">
                {result.correct === result.total
                  ? "满分！整体掌握非常好。"
                  : result.correct >= Math.ceil(result.total / 2)
                    ? "及格，继续查漏补缺。"
                    : "建议系统复习后再测。"}
              </div>
              <div className="mt-1 h-2 w-48 overflow-hidden rounded-full bg-secondary">
                <div
                  className={cn(
                    "h-full rounded-full",
                    result.correct === result.total
                      ? "bg-success"
                      : result.correct >= Math.ceil(result.total / 2)
                        ? "bg-primary"
                        : "bg-destructive"
                  )}
                  style={{ width: `${(result.correct / result.total) * 100}%` }}
                />
              </div>
            </div>

            <div className="space-y-3">
              {session?.questions.map((qq, i) => {
                const r = resultById.get(qq.exercise_id);
                return (
                  <div
                    key={qq.exercise_id}
                    className={cn(
                      "rounded-lg border p-3 text-sm",
                      r?.correct ? "border-success/30 bg-success/5" : "border-destructive/30 bg-destructive/5"
                    )}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className={cn(
                          "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full",
                          r?.correct ? "bg-success/15 text-success" : "bg-destructive/15 text-destructive"
                        )}
                      >
                        {r?.correct ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                      </span>
                      <div className="min-w-0">
                        <div className="font-medium">{i + 1}. {qq.question}</div>
                        {!r?.correct && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            你的答案：{r?.answer || "未作答"} · 正确：{r?.correct_answer}
                          </div>
                        )}
                        {r?.explanation && (
                          <div className="mt-1 text-xs text-muted-foreground">{r.explanation}</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => start(session?.total || 10)}>
                <RefreshCcw className="h-4 w-4" />
                再来一轮
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
              <NoteDrawer pageId="quiz" />
    </main>
  );
}